import React, {useEffect, useState} from 'react';
import {Box, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography, useMediaQuery, useTheme,} from '@mui/material';
import {CalendarMonth, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Person as PersonIcon, Today as TodayIcon,} from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/de';

// Typ für einen einzelnen Urlaubsantrag
interface Urlaubsantrag {
    id: string;
    username: string;
    beginn: string;
    ende: string;
    grund: string;
    notiz?: string;
    status: string;
    bearbeitung_datum?: string;
    bearbeiter_name?: string;
}

// Props für den Urlaubskalender
interface UrlaubskalenderProps {
    loading: boolean;
    antraege: Urlaubsantrag[];
}

export const Urlaubskalender: React.FC<UrlaubskalenderProps> = ({loading, antraege}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    // State für den aktuell angezeigten Monat
    const [currentDate, setCurrentDate] = useState(dayjs());

    // Initialisiere dayjs mit deutscher Lokalisierung
    useEffect(() => {
        dayjs.locale('de');
    }, []);

    const calendarAntraege = React.useMemo(
        () => antraege,
        [antraege, currentDate]
    );

    const displayedMonthAntraege = React.useMemo(() => {
        const monthStart = currentDate.startOf('month');
        const monthEnd = currentDate.endOf('month');

        return calendarAntraege.filter((antrag) => {
            const beginnDate = dayjs(antrag.beginn);
            const endeDate = dayjs(antrag.ende);

            return !endeDate.isBefore(monthStart, 'day') && !beginnDate.isAfter(monthEnd, 'day');
        });
    }, [calendarAntraege, currentDate]);

    const uniqueUsers = React.useMemo(
        () =>
            Array.from(new Set(displayedMonthAntraege.map((antrag) => antrag.username))).sort((a, b) =>
                a.localeCompare(b, 'de')
            ),
        [displayedMonthAntraege]
    );

    // Navigiere zum vorherigen Monat
    const goToPreviousMonth = () => {
        setCurrentDate(currentDate.subtract(1, 'month'));
    };

    // Navigiere zum nächsten Monat
    const goToNextMonth = () => {
        setCurrentDate(currentDate.add(1, 'month'));
    };

    // Navigiere zum aktuellen Monat
    const goToCurrentMonth = () => {
        setCurrentDate(dayjs());
    };

    // Generiere Kalendertage für den aktuellen Monat
    const generateCalendarDays = () => {
        const daysInMonth = currentDate.daysInMonth();
        const firstDayOfMonth = currentDate.startOf('month').day(); // 0 = Sonntag, 1 = Montag, ...

        // Anpassen, damit Montag der erste Tag der Woche ist
        const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

        const days = [];

        // Leere Zellen für Tage vor dem ersten Tag des Monats
        for (let i = 0; i < adjustedFirstDay; i++) {
            days.push(null);
        }

        // Tage des aktuellen Monats
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }

        return days;
    };

    // Prüfe, ob ein bestimmter Tag einen Urlaubsantrag hat
    const getUrlaubForDay = (day: number) => {
        if (!day) return [];

        const date = currentDate.date(day);

        return displayedMonthAntraege.filter((antrag) => {
            const beginnDate = dayjs(antrag.beginn);
            const endeDate = dayjs(antrag.ende);

            return (
                date.isSame(beginnDate, 'day') ||
                date.isSame(endeDate, 'day') ||
                (date.isAfter(beginnDate, 'day') && date.isBefore(endeDate, 'day'))
            );
        });
    };

    // Überprüft, ob ein Tag der Anfang eines Urlaubsantrags ist
    const isStartOfUrlaub = (day: number, antrag: Urlaubsantrag) => {
        if (!day) return false;
        const date = currentDate.date(day);
        return date.isSame(dayjs(antrag.beginn), 'day');
    };

    // Überprüft, ob ein Tag das Ende eines Urlaubsantrags ist
    const isEndOfUrlaub = (day: number, antrag: Urlaubsantrag) => {
        if (!day) return false;
        const date = currentDate.date(day);
        return date.isSame(dayjs(antrag.ende), 'day');
    };

    // Überprüft, ob ein Tag in der Mitte eines Urlaubsantrags ist
    const isMiddleOfUrlaub = (day: number, antrag: Urlaubsantrag) => {
        if (!day) return false;
        const date = currentDate.date(day);
        return date.isAfter(dayjs(antrag.beginn), 'day') && date.isBefore(dayjs(antrag.ende), 'day');
    };

    // Prüft, ob ein Antrag bereits genehmigt wurde
    const isApproved = (antrag: Urlaubsantrag) => antrag.status === 'Genehmigt';

    // Bestimmt die Position eines Tages in einer Urlaubsperiode
    const getUrlaubPosition = (day: number, antrag: Urlaubsantrag) => {
        if (!day) return '';

        const isStart = isStartOfUrlaub(day, antrag);
        const isEnd = isEndOfUrlaub(day, antrag);
        const isMiddle = isMiddleOfUrlaub(day, antrag);

        // Vereinfachte Logik für die Positionierung
        if (isStart && isEnd) return 'single';
        if (isStart) return 'start';
        if (isEnd) return 'end';
        if (isMiddle) return 'middle';
        return '';
    };

    // Erstelle ein konsistentes Mapping von Urlaub zu Position in der Zelle
    const createUrlaubPositionMapping = () => {
        const mapping = new Map<string, number>();
        let position = 0;

        // Gruppiere Anträge nach Benutzernamen
        const userAntraege = new Map<string, Urlaubsantrag[]>();

        antraege.forEach((antrag) => {
            if (!userAntraege.has(antrag.username)) {
                userAntraege.set(antrag.username, []);
            }
            userAntraege.get(antrag.username)?.push(antrag);
        });

        // Sortiere Benutzer alphabetisch
        const sortedUsers = Array.from(userAntraege.keys()).sort();

        // Weise jedem Benutzer-Antrag eine feste Position zu
        sortedUsers.forEach((username) => {
            const userUrlaube = userAntraege.get(username) || [];

            // Sortiere die Anträge des Benutzers nach Beginn-Datum
            userUrlaube
                .sort((a, b) => dayjs(a.beginn).valueOf() - dayjs(b.beginn).valueOf())
                .forEach((antrag) => {
                    mapping.set(antrag.id, position);
                    position += 1;
                });
        });

        return mapping;
    };

    // Erstelle ein Mapping von Benutzernamen zu Position
    const createUserPositionMapping = () => {
        const mapping = new Map<string, number>();

        // Gruppiere Anträge nach Benutzernamen
        const uniqueUsernames = [...new Set(antraege.map((antrag) => antrag.username))].sort();

        // Weise jedem Benutzer eine feste Position zu
        uniqueUsernames.forEach((username, index) => {
            mapping.set(username, index);
        });

        return mapping;
    };

    // Generiere eine eindeutige Farbe für jeden Benutzer
    const getUserColor = (username: string) => {
        // Einfache Hash-Funktion für den Benutzernamen
        let hash = 0;
        for (let i = 0; i < username.length; i++) {
            hash = username.charCodeAt(i) + ((hash << 5) - hash);
        }

        // Konvertiere Hash zu HEX-Farbe und stelle sicher, dass sie hell genug ist
        let color = '#';
        for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xff;
            // Stelle sicher, dass der Wert zwischen 100 und 200 liegt (helle, aber nicht zu helle Farben)
            const adjustedValue = Math.min(Math.max(100, value), 200).toString(16);
            color += ('00' + adjustedValue).substr(-2);
        }

        return color;
    };

    // Prüft, ob mindestens ein nicht genehmigter Antrag vorhanden ist
    const hasPendingAntraege = displayedMonthAntraege.some((a) => !isApproved(a));

    // Generiert eine Legende mit Farben und Benutzernamen
    const renderLegend = () => {
        if (uniqueUsers.length === 0) {
            return null;
        }

        const desktopLegendRows = uniqueUsers.length > 7 ? 2 : 1;

        return (
            <Box
                sx={{
                    mb: isMobile ? 2 : 0.75,
                    display: isMobile ? 'block' : 'flex',
                    alignItems: isMobile ? 'stretch' : 'flex-start',
                    gap: isMobile ? 0 : 1,
                    flex: '0 0 auto',
                    minHeight: isMobile ? 'auto' : desktopLegendRows === 2 ? 54 : 24,
                }}>
                <Typography
                    variant={isMobile ? 'subtitle2' : 'caption'}
                    sx={{
                        mb: isMobile ? 1 : 0,
                        flex: '0 0 auto',
                        color: isMobile ? 'inherit' : 'text.secondary',
                        fontWeight: isMobile ? 400 : 600,
                        whiteSpace: 'nowrap',
                    }}>
                    {isMobile ? 'Mitarbeiter:' : 'Mitarbeiter:'}
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        display: isMobile ? 'flex' : 'grid',
                        gridAutoFlow: isMobile ? undefined : 'column',
                        gridTemplateRows: isMobile ? undefined : `repeat(${desktopLegendRows}, 24px)`,
                        gridAutoColumns: isMobile ? undefined : 'max-content',
                        flexWrap: isMobile ? 'wrap' : undefined,
                        gap: isMobile ? 1 : 0.75,
                        overflowX: isMobile ? 'visible' : 'auto',
                        overflowY: 'hidden',
                        pb: isMobile ? 0 : 0.25,
                        minWidth: 0,
                        '&::-webkit-scrollbar': {
                            height: 4,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            borderRadius: 4,
                        },
                    }}>
                    {uniqueUsers.map((user) => (
                        <Box
                            key={user}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: getUserColor(user),
                                borderRadius: 1,
                                px: isMobile ? 1 : 0.9,
                                py: isMobile ? 0.5 : 0.25,
                                height: isMobile ? 'auto' : 24,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                                transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
                                '&:hover': {
                                    boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                                },
                            }}>
                            {!isMobile && <PersonIcon sx={{mr: 0.5, color: 'white', fontSize: 16}}/>}
                            <Typography
                                variant={isMobile ? 'body2' : 'body1'}
                                sx={{
                                    color: 'white',
                                    fontWeight: 500,
                                    fontSize: isMobile ? undefined : '0.75rem',
                                    lineHeight: 1.2,
                                    textShadow: '0px 1px 2px rgba(0,0,0,0.2)',
                                    whiteSpace: 'nowrap',
                                }}>
                                {user}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                {hasPendingAntraege && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mt: isMobile ? 1.5 : 0,
                            gap: 1,
                            flex: '0 0 auto',
                        }}>
                        <Box
                            sx={{
                                width: isMobile ? 22 : 18,
                                height: isMobile ? 14 : 11,
                                borderRadius: 0.5,
                                bgcolor: 'color-mix(in srgb, #9e9e9e 35%, white)',
                                backgroundImage:
                                    'repeating-linear-gradient(135deg, rgba(0,0,0,0.18) 0 6px, transparent 6px 12px)',
                                border: '1px dashed #9e9e9e',
                            }}
                        />
                        <Typography variant='caption' color='text.secondary'>
                            {isMobile
                                ? 'Noch nicht genehmigte Anträge sind ausgegraut dargestellt'
                                : 'Ausstehend'}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                <CircularProgress/>
            </Box>
        );
    }

    const monthDays = Array.from({length: currentDate.daysInMonth()}, (_, index) => index + 1);
    const getWeekdayLabel = (day: number) => currentDate.date(day).format('dd');
    const isWeekendDay = (day: number) => {
        const weekday = currentDate.date(day).day();
        return weekday === 0 || weekday === 6;
    };

    const getUrlaubForUserAndDay = (username: string, day: number) =>
        getUrlaubForDay(day).find((antrag) => antrag.username === username);
    const employeeRowHeight =
        uniqueUsers.length > 20 ? 30 : uniqueUsers.length > 14 ? 35 : uniqueUsers.length > 8 ? 42 : 50;

    // Mobiles Layout
    if (isMobile) {
        return (
            <Box sx={{p: 1}}>
                <Paper
                    elevation={0}
                    sx={{
                        mb: 2,
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff',
                        borderRadius: 1,
                        overflow: 'hidden',
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1.5,
                        }}>
                        <IconButton onClick={goToPreviousMonth} sx={{color: '#fff'}}>
                            <ChevronLeftIcon/>
                        </IconButton>
                        <Box display='flex' alignItems='center'>
                            <CalendarMonth sx={{mr: 1}}/>
                            <Typography variant='h6' sx={{fontWeight: 500}}>
                                {currentDate.format('MMMM YYYY')}
                            </Typography>
                        </Box>
                        <Box display='flex'>
                            <IconButton
                                onClick={goToCurrentMonth}
                                sx={{
                                    color: '#fff',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    mr: 1,
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                    },
                                }}>
                                <TodayIcon/>
                            </IconButton>
                            <IconButton onClick={goToNextMonth} sx={{color: '#fff'}}>
                                <ChevronRightIcon/>
                            </IconButton>
                        </Box>
                    </Box>
                </Paper>

                {/* Legende - Mobile */}
                {renderLegend()}

                {/* Liste der Urlaubsanträge nach Datum sortiert */}
                <Typography variant='subtitle1' sx={{mb: 1}}>
                    Urlaubsübersicht:
                </Typography>
                {antraege
                    .slice()
                    .sort((a, b) => dayjs(a.beginn).valueOf() - dayjs(b.beginn).valueOf())
                    .map((antrag) => {
                        const userColor = getUserColor(antrag.username);
                        const approved = isApproved(antrag);
                        return (
                            <Paper
                                key={antrag.id}
                                sx={{
                                    mb: 1,
                                    p: 1.5,
                                    borderLeft: `4px solid ${userColor}`,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                                    transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
                                    ...(!approved && {
                                        opacity: 0.55,
                                        filter: 'grayscale(0.5)',
                                        borderStyle: 'dashed',
                                    }),
                                    '&:hover': {
                                        boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                                    },
                                }}>
                                <Box sx={{display: 'flex', alignItems: 'center', mb: 0.5}}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            backgroundColor: userColor,
                                            mr: 1,
                                        }}
                                    />
                                    <Typography variant='subtitle2'>{antrag.username}</Typography>
                                </Box>
                                <Box display='flex' alignItems='center' mt={1} gap={0.5} flexWrap='wrap'>
                                    <Chip
                                        size='small'
                                        label={`${dayjs(antrag.beginn).format('DD.MM.YYYY')} - ${dayjs(
                                            antrag.ende
                                        ).format('DD.MM.YYYY')}`}
                                        sx={{
                                            height: 24,
                                            fontSize: '0.75rem',
                                            bgcolor: 'rgba(0,0,0,0.05)',
                                        }}
                                    />
                                    {!approved && (
                                        <Chip
                                            size='small'
                                            label={antrag.status || 'Nicht genehmigt'}
                                            sx={{
                                                height: 24,
                                                fontSize: '0.75rem',
                                                bgcolor: 'rgba(0,0,0,0.05)',
                                                fontStyle: 'italic',
                                            }}
                                        />
                                    )}
                                </Box>
                                {antrag.grund && (
                                    <Typography variant='body2' color='text.secondary' mt={0.5}>
                                        Grund: {antrag.grund}
                                    </Typography>
                                )}
                            </Paper>
                        );
                    })}
            </Box>
        );
    }

    // Desktop Layout
    return (
        <Box
            sx={{
                width: '90vw',
                maxWidth: '90vw',
                minHeight: '80vh',
                ml: 'calc(50% - 45vw)',
                mr: 'calc(50% - 45vw)',
                px: {md: 2, lg: 3},
                py: 1,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
            }}>
            <Paper
                elevation={0}
                sx={{
                    mb: 1,
                    flex: '0 0 auto',
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 0.75,
                        minHeight: 40,
                    }}>
                    <IconButton size='small' onClick={goToPreviousMonth} sx={{color: '#fff'}}>
                        <ChevronLeftIcon/>
                    </IconButton>
                    <Box display='flex' alignItems='center'>
                        <CalendarMonth sx={{mr: 1}}/>
                        <Typography variant='h6' sx={{fontWeight: 500}}>
                            {currentDate.format('MMMM YYYY')}
                        </Typography>
                    </Box>
                    <Box display='flex'>
                        <IconButton
                            size='small'
                            onClick={goToCurrentMonth}
                            sx={{
                                color: '#fff',
                                bgcolor: 'rgba(255,255,255,0.1)',
                                mr: 1,
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                },
                            }}>
                            <TodayIcon/>
                        </IconButton>
                        <IconButton size='small' onClick={goToNextMonth} sx={{color: '#fff'}}>
                            <ChevronRightIcon/>
                        </IconButton>
                    </Box>
                </Box>
            </Paper>

            {/* Kalender */}
            <Box
                sx={{
                    flex: '1 0 auto',
                    minHeight: 'calc(80vh - 64px)',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.16)',
                    overflow: 'visible',
                }}>
                <Box
                    sx={{
                        minWidth: 1180,
                        minHeight: 'calc(80vh - 64px)',
                        display: 'grid',
                        gridTemplateColumns: `220px repeat(${monthDays.length}, minmax(34px, 1fr))`,
                        gridTemplateRows: `42px repeat(${Math.max(uniqueUsers.length, 1)}, ${employeeRowHeight}px)`,
                        alignContent: 'start',
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.25,
                            fontWeight: 700,
                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                            borderRight: '1px solid',
                            borderRightColor: theme.palette.divider,
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: theme.palette.primary.main,
                        }}>
                        Mitarbeiter
                    </Box>
                    {monthDays.map((day) => {
                        const isWeekend = isWeekendDay(day);
                        const isToday =
                            dayjs().year() === currentDate.year() &&
                            dayjs().month() === currentDate.month() &&
                            dayjs().date() === day;

                        return (
                            <Box
                                key={`day-header-${day}`}
                                sx={{
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: isWeekend ? 'rgba(0, 0, 0, 0.045)' : 'rgba(25, 118, 210, 0.08)',
                                    borderRight: day === monthDays.length ? 'none' : '1px solid',
                                    borderRightColor: theme.palette.divider,
                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    color: isWeekend ? 'text.secondary' : theme.palette.primary.main,
                                    ...(isToday && {
                                        outline: '2px solid #ef5350',
                                        outlineOffset: '-2px',
                                        bgcolor: '#ffebee',
                                        color: '#b71c1c',
                                    }),
                                }}>
                                <Typography sx={{fontSize: '0.95rem', fontWeight: 700, lineHeight: 1}}>
                                    {day}
                                </Typography>
                                <Typography sx={{fontSize: '0.68rem', lineHeight: 1.1}}>
                                    {getWeekdayLabel(day)}
                                </Typography>
                            </Box>
                        );
                    })}

                    {uniqueUsers.length === 0 && (
                        <Box
                            sx={{
                                gridColumn: `1 / span ${monthDays.length + 1}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.secondary',
                            }}>
                            Keine Urlaubsanträge in diesem Monat
                        </Box>
                    )}

                    {uniqueUsers.map((username, rowIndex) => (
                        <React.Fragment key={username}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: 0,
                                    px: 1,
                                    gap: 0.75,
                                    bgcolor: rowIndex % 2 === 0 ? 'rgba(0,0,0,0.015)' : 'background.paper',
                                    borderRight: '1px solid',
                                    borderRightColor: theme.palette.divider,
                                    borderBottom: rowIndex === uniqueUsers.length - 1 ? 'none' : '1px solid',
                                    borderBottomColor: theme.palette.divider,
                                }}>
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        bgcolor: getUserColor(username),
                                        flex: '0 0 auto',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.88rem',
                                        fontWeight: 600,
                                    }}>
                                    {username}
                                </Typography>
                            </Box>

                            {monthDays.map((day) => {
                                const antrag = getUrlaubForUserAndDay(username, day);
                                const userColor = getUserColor(username);
                                const approved = antrag ? isApproved(antrag) : false;
                                const position = antrag ? getUrlaubPosition(day, antrag) : '';
                                const isWeekend = isWeekendDay(day);

                                const cell = (
                                    <Box
                                        key={`${username}-${day}`}
                                        sx={{
                                            minWidth: 0,
                                            minHeight: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.76rem',
                                            fontWeight: 700,
                                            color: antrag ? (approved ? '#fff' : '#1f1f1f') : 'transparent',
                                            bgcolor: antrag
                                                ? approved
                                                    ? userColor
                                                    : `color-mix(in srgb, ${userColor} 38%, white)`
                                                : isWeekend
                                                    ? 'rgba(0, 0, 0, 0.035)'
                                                    : rowIndex % 2 === 0
                                                        ? 'rgba(0,0,0,0.015)'
                                                        : 'background.paper',
                                            borderRight: day === monthDays.length ? 'none' : '1px solid',
                                            borderRightColor: theme.palette.divider,
                                            borderBottom: rowIndex === uniqueUsers.length - 1 ? 'none' : '1px solid',
                                            borderBottomColor: theme.palette.divider,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            cursor: antrag ? 'pointer' : 'default',
                                            ...(antrag &&
                                                !approved && {
                                                    backgroundImage: `repeating-linear-gradient(135deg, ${userColor}45 0 6px, transparent 6px 12px)`,
                                                    borderTop: `1px dashed ${userColor}`,
                                                }),
                                            ...(position === 'single' && {
                                                borderRadius: '4px',
                                                mx: 0.25,
                                            }),
                                            ...(position === 'start' && {
                                                borderRadius: '4px 0 0 4px',
                                                ml: 0.25,
                                            }),
                                            ...(position === 'end' && {
                                                borderRadius: '0 4px 4px 0',
                                                mr: 0.25,
                                            }),
                                        }}>
                                        {''}
                                    </Box>
                                );

                                if (!antrag) {
                                    return cell;
                                }

                                return (
                                    <Tooltip
                                        key={`${username}-${day}-tooltip`}
                                        title={
                                            <Box>
                                                <Typography variant='body2'>
                                                    <strong>{antrag.username}</strong>
                                                </Typography>
                                                <Typography variant='body2'>
                                                    {dayjs(antrag.beginn).format('DD.MM.YYYY')} -{' '}
                                                    {dayjs(antrag.ende).format('DD.MM.YYYY')}
                                                </Typography>
                                                {antrag.grund && (
                                                    <Typography variant='body2'>Grund: {antrag.grund}</Typography>
                                                )}
                                                {antrag.notiz && (
                                                    <Typography variant='body2'>Notiz: {antrag.notiz}</Typography>
                                                )}
                                                <Typography variant='body2'>Status: {antrag.status}</Typography>
                                            </Box>
                                        }
                                        arrow>
                                        {cell}
                                    </Tooltip>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
